import { type HttpsCallableOptions, httpsCallable } from 'firebase/functions'

import { getFirebaseFunctions } from './app'
import { parseCallableError } from './errors'

/** Drops keys whose value is `undefined`, recursively; the transport turns `undefined` into `null`, which server `.optional()` schemas reject. `null` is kept. */
export function withoutUndefined<T>(value: T): T {
  if (Array.isArray(value)) return value.map(withoutUndefined) as T
  if (value === null || typeof value !== 'object') return value
  // Dates and other class instances are serialized by the transport, never rebuilt here.
  if (Object.getPrototypeOf(value) !== Object.prototype) return value

  const result: Record<string, unknown> = {}
  for (const [key, entry] of Object.entries(value)) {
    if (entry !== undefined) result[key] = withoutUndefined(entry)
  }
  return result as T
}

/** Creates a typed wrapper around a Cloud Functions callable, rejecting with a `CallableError`; `options.timeout` must cover a callable that runs past the SDK's 70 s default. */
export function callable<TIn, TOut>(
  name: string,
  options?: HttpsCallableOptions,
): (input: TIn) => Promise<TOut> {
  return async (input: TIn): Promise<TOut> => {
    try {
      const fn = httpsCallable<TIn, TOut>(getFirebaseFunctions(), name, options)
      const result = await fn(withoutUndefined(input))
      return result.data
    } catch (error) {
      throw parseCallableError(error)
    }
  }
}
